import { useState, useEffect } from 'react';
import { advisorService } from '../services/apiService';

const AdvisorForm = ({ advisor, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    designation: '',
    bio: '',
    linkedIn: '',
    anyMoreLink: '',
    detailInfo: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    if (advisor) {
      setFormData({
        name: advisor.name,
        location: advisor.location,
        designation: advisor.designation,
        bio: advisor.bio,
        linkedIn: advisor.linkedIn,
        anyMoreLink: advisor.anyMoreLink || '',
        detailInfo: advisor.detailInfo
      });
      setPreviewImage(advisor.image ? `data:image/jpeg;base64,${advisor.image}` : null);
    }
  }, [advisor]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPEG, PNG, etc.)');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
      
      setImageFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const requiredFields = ['name', 'location', 'designation', 'bio', 'linkedIn', 'detailInfo'];
      const missingFields = requiredFields.filter(field => !formData[field].trim());
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const formDataToSend = new FormData();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (!advisor) {
        throw new Error('Please select an image');
      }

      if (advisor) {
        await advisorService.updateAdvisor(advisor._id, formDataToSend);
      } else {
        await advisorService.createAdvisor(formDataToSend);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save advisor. Please check all required fields and try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {error && (
        <div className="p-2 mb-4 text-red-600 bg-red-100 rounded">
          {error}
        </div>
      )}

      {/* Image Upload */}
      <div>
        <label className="block mb-2 font-medium">
          {advisor ? 'Update Image (Optional)' : 'Upload Image*'}
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#166534]/90"
          required={!advisor}
        />
        {!imageFile && advisor?.image && (
          <p className="mt-1 text-sm text-gray-500">Current image will be kept</p>
        )}
      </div>

      {/* Image Preview */}
      {(previewImage || (advisor?.image && !imageFile)) && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-1">Preview:</p>
          <img 
            src={previewImage || `data:image/jpeg;base64,${advisor.image}`}
            className="h-24 w-24 object-cover rounded border border-gray-200"
            alt="Preview"
          />
        </div>
      )}

      {/* Name Field */}
      <div>
        <label className="block mb-1 font-medium">Name*</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      {/* Location Field */}
      <div>
        <label className="block mb-1 font-medium">Location*</label>
        <input
          type="text"
          name="location"
          value={formData.location}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      {/* Designation Field */}
      <div>
        <label className="block mb-1 font-medium">Designation*</label>
        <input
          type="text"
          name="designation"
          value={formData.designation}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      {/* Bio Field */}
      <div>
        <label className="block mb-1 font-medium">Bio*</label>
        <textarea
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          rows={3}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      {/* LinkedIn Field */}
      <div>
        <label className="block mb-1 font-medium">LinkedIn URL*</label>
        <input
          type="url"
          name="linkedIn"
          value={formData.linkedIn}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      {/* Any More Link Field */}
      <div>
        <label className="block mb-1 font-medium">Any More Link</label>
        <input
          type="url"
          name="anyMoreLink"
          value={formData.anyMoreLink}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
        />
      </div>

      {/* Detail Info Field */}
      <div>
        <label className="block mb-1 font-medium">Detailed Information*</label>
        <textarea
          name="detailInfo"
          value={formData.detailInfo}
          onChange={handleChange}
          rows={5}
          className="w-full p-2 border border-gray-300 rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-white bg-[#166534] rounded hover:bg-[#166534]/90 disabled:opacity-70 flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : advisor ? 'Update Advisor' : 'Add Advisor'}
        </button>
      </div>
    </form>
  );
};

export default AdvisorForm;
